import { useCallback, useState } from "react";

/**
 * Click-driven popup state for landing-page feature cards.
 *
 * Behavior:
 *  - Cards are static until clicked (or activated via Enter/Space when focused).
 *  - `handleSelect(key)` opens the dialog immediately for that card.
 *  - Closing is handled by the dialog itself (X, overlay click, Escape).
 *
 * The hook used to also support hover-intent open/close, but that felt twitchy
 * on dense card grids and broke on touch — click-only is more deliberate and
 * works everywhere. The export name is kept for backwards compatibility with
 * existing imports.
 */
export function useFeatureHoverPopup<K extends string>() {
  const [open, setOpen] = useState(false);
  const [activeKey, setActiveKey] = useState<K | null>(null);

  const handleSelect = useCallback((key: K) => {
    setActiveKey(key);
    setOpen(true);
  }, []);

  return {
    open,
    setOpen,
    activeKey,
    handleSelect,
  };
}
